/**
 * Navigation Helper Utilities
 * Provides utilities for setting up the sci-nav component with user data
 */

import type { Guardian, Student } from './types';

/**
 * Update the sci-nav component with user information (Guardian or Student)
 *
 * @param user - The authenticated user object (Guardian or Student)
 * @param navElement - Optional sci-nav element (defaults to first sci-nav on page)
 *
 * @example
 * const guardian = await requireAuth('parent');
 * updateNavWithUser(guardian);
 */
export function updateNavWithUser(user: Guardian | Student, navElement?: HTMLElement): void {
  // Get nav element
  const nav = navElement || document.querySelector('sci-nav');

  if (!nav) {
    console.warn('sci-nav element not found');
    return;
  }

  // Set user name
  nav.setAttribute('user-name', user.full_name);

  // Set account type (guardian_type or 'student')
  const accountType = 'guardian_type' in user ? user.guardian_type : 'student';
  nav.setAttribute('account-type', accountType);

  // Set mode to authenticated
  nav.setAttribute('mode', 'authenticated');

  // Set avatar if available
  if (user.avatar) {
    nav.setAttribute('user-avatar', user.avatar);
  }
}

/**
 * Update the sci-nav component with student information
 * Used when viewing student-specific pages
 *
 * @param student - The student object
 * @param navElement - Optional sci-nav element (defaults to first sci-nav on page)
 *
 * @example
 * const student = StudentService.getCurrentStudent();
 * updateNavWithStudent(student);
 */
export function updateNavWithStudent(student: Student, navElement?: HTMLElement): void {
  // Get nav element
  const nav = navElement || document.querySelector('sci-nav');

  if (!nav) {
    console.warn('sci-nav element not found');
    return;
  }

  // Set user name
  nav.setAttribute('user-name', student.full_name);

  // Set account type
  nav.setAttribute('account-type', 'student');

  // Set mode to authenticated
  nav.setAttribute('mode', 'authenticated');

  // Set avatar if available
  if (student.avatar) {
    nav.setAttribute('user-avatar', student.avatar);
  }
}

/**
 * Get the sci-nav element
 * Helper function to safely get the nav element
 *
 * @returns The sci-nav element or null if not found
 */
export function getNavElement(): HTMLElement | null {
  return document.querySelector('sci-nav');
}
